/**
 * The language the person at this screen reads the app in.
 *
 * Their account keeps the choice; the server is where it lives. This browser remembers it
 * so the app opens in the right language at once. A page is written in ONE language from
 * the moment it loads: choosing another one remembers it and loads the page again.
 */
import { asWritten, makeTranslator, type Translate } from './translate';

export type Language = 'en' | 'es';

export const LANGUAGES: readonly { code: Language; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
];

const REMEMBERED_KEY = 'qmi.language';

export function isLanguage(value: unknown): value is Language {
  return value === 'en' || value === 'es';
}

function remembered(): Language {
  try {
    const kept = globalThis.localStorage.getItem(REMEMBERED_KEY);
    return isLanguage(kept) ? kept : 'en';
  } catch {
    return 'en';
  }
}

const active: Language = remembered();
let translate: Translate = asWritten;

export function activeLanguage(): Language {
  return active;
}

/**
 * Called once, before the first screen is built: fetches the dictionary of the language
 * this page is in. English is what the screens are written in and needs none.
 */
export async function prepareLanguage(): Promise<void> {
  if (typeof document !== 'undefined') document.documentElement.lang = active;
  if (active === 'es') translate = makeTranslator((await import('./es.json')).default);
}

/**
 * Shows the app in `language` from now on: remembered in this browser, and the page is
 * loaded again in it. Does nothing when the page is already in that language — or when
 * the browser cannot remember, since loading again would change nothing.
 */
export function showIn(language: Language): void {
  if (language === active) return;
  try {
    globalThis.localStorage.setItem(REMEMBERED_KEY, language);
  } catch {
    return;
  }
  globalThis.location.reload();
}

/**
 * The locale dates and numbers are written in: the reader's language when they chose
 * Spanish, otherwise whatever their browser uses (`undefined`), as it always was.
 */
export function readerLocale(): string | undefined {
  return active === 'es' ? 'es-MX' : undefined;
}

/**
 * The text as the person reads it. The screens get this for free (see `jsx-runtime`);
 * call it for text that never passes through a screen — a browser confirmation, the
 * title of the tab — and for a word that means two things in English: `t('Open', 'verb')`
 * is looked up as "Open ⟨verb⟩", and falls back to plain "Open".
 */
export function t(text: string, meaning?: string): string {
  if (meaning !== undefined) {
    const marked = `${text} ⟨${meaning}⟩`;
    const found = translate(marked);
    if (found !== marked) return found;
  }
  return translate(text);
}
